/**
 * The public pages' frame (Figma 10): the still on the left at desktop, a band
 * under the top bar on tablet and phone, the 400 column centred in the side,
 * the footer at its foot. Light and dark follow the device until the person
 * picks one (Settings → Appearance), like the product.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useResolvedTheme } from "@/stores/appearanceStore";
import { CoShellPortalContext } from "@/app/routes/dashboard/CoShellPortalContext";
import "@/app/routes/dashboard/cockpit.css";
import "./gettingIn.css";
import { Footer, TopBar } from "./parts";
import sitting from "@/assets/images/getting-in/sitting.jpg";
import sittingWide from "@/assets/images/getting-in/sitting-wide.jpg";
import network from "@/assets/images/getting-in/network.jpg";
import networkWide from "@/assets/images/getting-in/network-wide.jpg";
import arches from "@/assets/images/getting-in/arches.jpg";
import archesWide from "@/assets/images/getting-in/arches-wide.jpg";

/** The three stills: sitting (sign in, first run), network (links, inbox), arches (join). */
export type StillName = "sitting" | "network" | "arches";

const STILLS: Record<StillName, { tall: string; wide: string; alt: string }> = {
  sitting: { tall: sitting, wide: sittingWide, alt: "" },
  network: { tall: network, wide: networkWide, alt: "" },
  arches: { tall: arches, wide: archesWide, alt: "" },
};

/** Past this the picture stays away: it never arrives late on a page in use (M08). */
const LATE_MS = 1500;

/**
 * Still (3:1213): the panel shows plain surface while the picture loads, then
 * the picture fades in once decoded. It never moves the form or blocks typing.
 */
export function Still({ name, wide = false, className }: { name: StillName; wide?: boolean; className?: string }) {
  const src = wide ? STILLS[name].wide : STILLS[name].tall;
  // Which picture has decoded in time; a new src starts plain again.
  const [readySrc, setReadySrc] = useState<string | null>(null);
  const ready = readySrc === src;

  useEffect(() => {
    const started = performance.now();
    let cancelled = false;
    const img = new Image();
    img.src = src;
    img
      .decode()
      .then(() => {
        if (!cancelled && performance.now() - started <= LATE_MS) setReadySrc(src);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <div className={`gi-still ${className ?? ""}`} data-ready={ready}>
      {ready ? <img src={src} alt={STILLS[name].alt} draggable={false} /> : null}
    </div>
  );
}

export function PublicShell({
  still,
  panel,
  band,
  topAction,
  hideTop = false,
  title,
  children,
}: {
  /** The picture for this page. */
  still?: StillName;
  /** Replaces the desktop still (the Join page's invitation panel). */
  panel?: ReactNode;
  /** Replaces the tablet/phone band. */
  band?: ReactNode;
  /** One quiet action at the end of the top bar ("Sign in", "Sign out"). */
  topAction?: ReactNode;
  /** The Join page draws no top bar: its invitation is the header. */
  hideTop?: boolean;
  /** document.title, "· Creo" appended. */
  title: string;
  children: ReactNode;
}) {
  const theme = useResolvedTheme();
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Creo`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  const hasPicture = Boolean(still || panel);

  return (
    <CoShellPortalContext.Provider value={portal}>
      <div ref={setPortal} className={`co-shell co-page${theme === "dark" ? " dark" : ""}`}>
        <div className="gi-page">
          {hasPicture ? (
            <aside className="gi-panel" aria-hidden={!panel}>
              {panel ?? (still ? <Still name={still} /> : null)}
            </aside>
          ) : null}
          <div className="gi-side">
            {hideTop ? null : <TopBar action={topAction} />}
            {hasPicture ? (
              <div className="gi-band" aria-hidden={!band}>
                {band ?? (still ? <Still name={still} wide /> : null)}
              </div>
            ) : null}
            <main className="gi-body">{children}</main>
            <div className="gi-foot">
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </CoShellPortalContext.Provider>
  );
}
